import React, { useMemo } from "react";
import { cn } from "utils";
import { useQuery } from "@tanstack/react-query";
import Segmented from "../../Segmented/Segmented";
import { ChatCategoryCardType } from "../Cards/type";
import CandleView, { CandleViewProps } from "./token/CandleView";
import { QUERY_KEYS } from "@/src/config";

interface LineHeaderEndProps extends Pick<CandleViewProps, "market_symbol"> {
  className?: string;
  setSelectedDataType?: (type: string) => void;
  cardItem?: ChatCategoryCardType;
  showSegmented?: boolean;
  dataType?: string;
  options?: { label: string; value: string }[];
  defaultValue?: string;
  isStock?: boolean;
}

export const DataType = {
  "1D": "24h",
  "7D": "7d",
  "30D": "30d",
};

const defaultOptions = [
  // { label: "1D", value: DataType["1D"] },
  { label: "7D", value: DataType["7D"] },
  { label: "30D", value: DataType["30D"] },
];

const LineHeaderEnd: React.FC<LineHeaderEndProps> = ({
  setSelectedDataType,
  cardItem,
  showSegmented = true,
  className = "",
  dataType,
  options,
  defaultValue,
  isStock = false,
}) => {
  const { data: symbolSearchData } = useQuery({
    queryKey: [QUERY_KEYS.SYMBOL_SEARCH, cardItem?.properties.token_symbol],
    enabled: !!cardItem?.properties.token_symbol,
    staleTime: 15 * 60 * 1000,
    queryFn: async () => {
      if (!cardItem?.properties.token_symbol) {
        return;
      }
      const searchText = isStock
        ? cardItem.properties.token_symbol
        : `${cardItem.properties.token_symbol}USDT`;
      const res = await fetch(`/api/symbol-search?text=${searchText}`);
      if (!res.ok) {
        throw new Error("Failed to fetch from proxy");
      }
      const data = await res.json();
      return data?.symbols;
    },
  });
  const market_symbol = useMemo(() => {
    if (!symbolSearchData?.length) {
      return null;
    }
    const firstMarket = symbolSearchData?.[0];
    if (
      !firstMarket.symbol.startsWith("<em>") ||
      !firstMarket.symbol.endsWith("</em>")
    ) {
      return null;
    }
    const market_name = firstMarket.source_id;
    if (!market_name) {
      return null;
    }

    return isStock
      ? `${market_name}:${cardItem?.properties.token_symbol}`
      : `${market_name}:${cardItem?.properties.token_symbol}USDT`;
  }, [symbolSearchData, cardItem?.properties.token_symbol, isStock]);

  const widgetPropsOptions = isStock
    ? {
        timezone: "America/New_York",
      }
    : {};

  const headerRight = (
    <div className={cn("flex flex-row items-center gap-3", className)}>
      {showSegmented && (
        <Segmented
          options={options || defaultOptions}
          onChange={setSelectedDataType}
          defaultValue={defaultValue || DataType["1D"]}
          value={dataType}
        />
      )}
      {market_symbol && (
        <CandleView
          cardItem={cardItem}
          market_symbol={market_symbol}
          widgetPropsOptions={widgetPropsOptions}
        />
      )}
    </div>
  );
  return headerRight;
};

export default LineHeaderEnd;
